import { useMemo } from 'react'
import { ThemeProvider, createTheme, alpha, lighten } from '@mui/material/styles'
import { useAppTheme } from '../hooks/useAppTheme'
import { DARK } from './themePalette'

/**
 * Мост между темой приложения (кука kw_app_theme) и темой MUI.
 *
 * Раньше MUI-провайдера в приложении не было вовсе: компоненты брали светлые
 * дефолты библиотеки, а тёмные цвета проставлялись руками через getTheme(dark).
 * Везде, где это забывали сделать (таблицы, чипы, плейсхолдеры, пагинация,
 * выпадающие меню), в тёмной теме получался тёмный текст на тёмном фоне.
 *
 * Палитра намеренно минимальная — только то, что отвечает за читаемость:
 * mode, фон, текст и разделители из общей палитры themePalette.
 *
 * Акцентные цвета закреплены в значениях светлой темы MUI. Это не косметика:
 * в тёмном режиме MUI по умолчанию осветляет primary (#1976d2 → #90caf9) и
 * пересчитывает contrastText в почти чёрный — закрашенные кнопки («Создать
 * промокод», «Найти», «Перейти») получали тёмный текст. С закреплённой
 * палитрой кнопки выглядят ровно так же, как до появления провайдера.
 */
const ACCENTS = {
  primary: { main: '#1976d2' },
  secondary: { main: '#9c27b0' },
  error: { main: '#d32f2f' },
  warning: { main: '#ed6c02' },
  info: { main: '#0288d1' },
  success: { main: '#2e7d32' },
}

/**
 * Осветлённый вариант акцента для тёмной темы. Возвращает null, если у элемента
 * нет своего акцента (color="default"/"inherit") — тогда цвет не трогаем.
 */
function accentTone(theme, color) {
  if (!color || color === 'default' || color === 'inherit') return null
  const main = theme.palette?.[color]?.main
  return main ? lighten(main, 0.45) : null
}

export default function MuiThemeBridge({ children }) {
  const { dark } = useAppTheme()

  const theme = useMemo(
    () =>
      createTheme({
        palette: dark
          ? {
              mode: 'dark',
              ...ACCENTS,
              // default — прозрачный: под страницами живёт глобальный
              // анимированный фон «Liquid Silk» (common/AnimatedBackground.jsx).
              background: { default: 'transparent', paper: DARK.paperSolid },
              text: { primary: DARK.text, secondary: DARK.textSec },
              divider: DARK.divider,
            }
          : { mode: 'light', ...ACCENTS },
        // У залитых элементов акцент — это фон, текст на нём белый, всё читается.
        // А у текстовых и контурных вариантов акцент становится цветом ТЕКСТА:
        // тёмно-синий #1976d2 или тёмно-зелёный #2e7d32 на почти чёрном фоне
        // читаются плохо (3.2:1 при норме 4.5:1). В тёмной теме осветляем
        // акцент именно для этих вариантов — заливки остаются прежними.
        components: dark
          ? {
              MuiButton: {
                styleOverrides: {
                  root: ({ ownerState, theme }) => {
                    if (ownerState.variant === 'contained') return {}
                    const tone = accentTone(theme, ownerState.color)
                    if (!tone) return {}
                    return {
                      color: tone,
                      ...(ownerState.variant === 'outlined' ? { borderColor: alpha(tone, 0.5) } : {}),
                    }
                  },
                },
              },
              MuiChip: {
                styleOverrides: {
                  root: ({ ownerState, theme }) => {
                    if (ownerState.variant !== 'outlined') return {}
                    const tone = accentTone(theme, ownerState.color)
                    if (!tone) return {}
                    return { color: tone, borderColor: alpha(tone, 0.5) }
                  },
                },
              },
            }
          : {},
      }),
    [dark],
  )

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>
}
